import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

// Simple inline SVG icons (no external deps)
const IconDashboard = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 3a9 9 0 00-9 9v6a3 3 0 003 3h12a3 3 0 003-3v-6a9 9 0 00-9-9zm0 2a7 7 0 017 7v6a1 1 0 01-1 1h-2v-3a4 4 0 00-8 0v3H6a1 1 0 01-1-1v-6a7 7 0 017-7zm0 6a1 1 0 110 2 1 1 0 010-2z"/>
  </svg>
);

const IconForecast = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M3 17h2.586l4.95-4.95 3.536 3.536L21 7.657V11h2V5h-6v2h3.343l-5.657 5.657-3.536-3.536L3 14.414V17z"/>
  </svg>
);

const IconExpenses = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M3 4h18a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm0 4v2h18V8H3zm0 6v4h18v-4H3z"/>
  </svg>
);

const IconInstallments = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M9 15l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconBudgets = (props) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M8 4h8v3a4 4 0 0 1-8 0V4z" />
    <path d="M6 4h12a2 2 0 0 1 2 2v1a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V6a2 2 0 0 1 2-2z" />
    <path d="M12 14v5" />
    <path d="M9 21h6" />
  </svg>
);

const IconSubscriptions = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M12 2a10 10 0 1 0 9.95 11h-2.02a8 8 0 1 1-7.93-9V7l4-4-4-4v3z" fill="currentColor"/>
  </svg>
);

const IconTags = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M3 12l8.586 8.586a2 2 0 002.828 0L21 14.001a2 2 0 000-2.828L12.414 3H5a2 2 0 00-2 2v7z" stroke="currentColor" strokeWidth="2" fill="none"/>
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
  </svg>
);

const IconTransfers = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M7 7h10m0 0-3-3m3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 17H7m0 0 3 3m-3-3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconUpload = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M4 18h16v2H4v-2zm8-14l5 5h-3v6h-4V9H7l5-5z"/>
  </svg>
);

const IconTreemap = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <rect x="3" y="3" width="8" height="8" stroke="currentColor" strokeWidth="2"/>
    <rect x="13" y="3" width="8" height="5" stroke="currentColor" strokeWidth="2"/>
    <rect x="13" y="10" width="8" height="11" stroke="currentColor" strokeWidth="2"/>
    <rect x="3" y="13" width="8" height="8" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Hamburger/Toggle icon
const IconToggle = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
  </svg>
);

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebarCollapsed', String(next)); } catch {}
      return next;
    });
  };

  const uploadItem = { to: '/upload', labelKey: 'nav.upload', fallback: 'Subir Cartolas', Icon: IconUpload };

  const renderItems = (arr) => (
    arr.map(({ to, labelKey, fallback, Icon }) => {
      const active = location.pathname === to;
      return (
        <li key={to} className={`sidebar-item ${active ? 'is-active' : ''}`}>
          <Link to={to} className="sidebar-link" data-tooltip={collapsed ? t(labelKey, fallback) : undefined}>
            <Icon className="sidebar-icon" />
            {!collapsed && <span className="sidebar-label">{t(labelKey, fallback)}</span>}
          </Link>
        </li>
      );
    })
  );

  return (
    <aside
      className={`dashboard-sidebar ${collapsed ? 'is-collapsed' : 'is-expanded'}`}
      role="navigation"
      aria-label={t('nav.menu', 'Menu')}
    >
      <div
        className={`sidebar-header ${collapsed ? 'is-collapsed' : 'is-expanded'}`}
        style={
          !collapsed
            ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
            : { cursor: 'pointer' }
        }
        onClick={collapsed ? toggleCollapsed : undefined}
      >
        {collapsed ? (
          <button
            type="button"
            className="sidebar-toggle icon-only"
            aria-label={t('nav.expand', 'Expand sidebar')}
            onClick={toggleCollapsed}
            data-tooltip={t('nav.menu', 'Menu')}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 0, display: 'inline-flex', position: 'relative' }}
          >
            <IconToggle className="sidebar-icon" onClick={toggleCollapsed} style={{ pointerEvents: 'auto' }} />
          </button>
        ) : (
          <>
            <h2
              className="sidebar-title"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', userSelect: 'none' }}
            >
              <button
                type="button"
                className="sidebar-toggle icon-only"
                aria-label={t('nav.collapse', 'Collapse sidebar')}
                onClick={toggleCollapsed}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 0, display: 'inline-flex' }}
              >
                <IconToggle className="sidebar-icon" />
              </button>
              <span onClick={toggleCollapsed} style={{ cursor: 'pointer', marginLeft: '0.5rem' }}>
                {t('nav.menu', 'Menu')}
              </span>
            </h2>
          </>
        )}
      </div>

      <ul className="sidebar-list">
        {/* Analysis group */}
        {renderItems([
          { to: '/dashboard', labelKey: 'nav.dashboard', fallback: 'Dashboard', Icon: IconDashboard },
          { to: '/forecast', labelKey: 'nav.forecast', fallback: 'Forecast', Icon: IconForecast },
          { to: '/treemap', labelKey: 'nav.treemap', fallback: 'Mapa de gastos', Icon: IconTreemap }
        ])}

        <li className="sidebar-upload-separator" role="separator" aria-hidden="true"></li>

        {/* Finances group */}
        {renderItems([
          { to: '/expenses', labelKey: 'nav.expenses', fallback: 'Expenses', Icon: IconExpenses },
          { to: '/installments', labelKey: 'nav.installments', fallback: 'Installments', Icon: IconInstallments },
          { to: '/transfers', labelKey: 'nav.transfers', fallback: 'Transferencias', Icon: IconTransfers },
          { to: '/subscriptions', labelKey: 'nav.subscriptions', fallback: 'Suscripciones', Icon: IconSubscriptions }
        ])}

        <li className="sidebar-upload-separator" role="separator" aria-hidden="true"></li>

        {/* Management group */}
        {renderItems([
          { to: '/goals', labelKey: 'nav.goals', fallback: 'Metas', Icon: IconBudgets },
          { to: '/tags', labelKey: 'nav.tags', fallback: 'Etiquetas', Icon: IconTags }
        ])}

        {/* Upload item */}
        <li className={`sidebar-item sidebar-upload ${location.pathname === uploadItem.to ? 'is-active' : ''}`}>
          <Link
            to={uploadItem.to}
            className="sidebar-link"
            data-tooltip={collapsed ? t(uploadItem.labelKey, uploadItem.fallback) : undefined}
          >
            <uploadItem.Icon className="sidebar-icon" />
            {!collapsed && (
              <span
                className="sidebar-label"
                style={{ whiteSpace: 'pre-line', textAlign: 'left', lineHeight: '1.2', display: 'block' }}
              >
                {t(uploadItem.labelKey, uploadItem.fallback).split(' ').join('\n')}
              </span>
            )}
          </Link>
        </li>
      </ul>
    </aside>
  );
}

export default Sidebar;
